import { KPIDrivers } from '@services/api/deckManagement/graphql/schema/API';
import { DriverDisplayOptionEntry, DriverDisplayOptionMap } from 'types/PageContext.types';

/**
 * Interface for defining Segment display data in the Page and Deckline Drivers Side Panel
 */
export interface SegmentDisplayData {
  kpiDimension: KpiDimension;
  value: string;
  hierarchy?: string | null;
  active: boolean;
}

/**
 * Driver types for page configuration in the side panel
 */
export enum DriverType {
  PAGE = 'page',
  DECKLINE = 'deckline',
  // For the `denominator_kpi_id_for_pct` field(s)
  DENOMINATOR_FOR_PERCENT = 'denominator_for_percent',
}

/**
 * Dropdown options for the side panel
 */
export enum PageSidePanelDropDown {
  NONE = 'N/A',
  BLANK_LINE = 'Blank Line',
  ADJUSTMENT = 'Adjustment',
  CALCULATED_ADJUSTMENT = 'Calculated Adjustment',
  BASE_KPI = 'Base KPI',
  CALCULATED_KPI = 'Calculated KPI',
  SUBTOTAL = 'Subtotal',
}

/**
 * The field types that are supported for given 'dimensions' on the side panel.
 * Most are DimensionPicker, but some can be other field types (such as Reporting Currency).
 * @see {@link KPI_DIMENSION_CONFIG_MAP} for implementation usage.
 */
export enum BaseKpiDimensionFieldTypes {
  /**
   * For KPI Dimensions supported by the DimensionPicker.
   * @see BaseKpiDimensionFieldTypeDimensionPicker
   */
  DimensionPicker = 'DimensionPicker',
  /**
   * For KPI Dimensions where there is a fixed list of available options.
   * @see BaseKpiDimensionFieldTypeSelect
   */
  Select = 'Select',
  /**
   * For KPI Dimensions when it is not configurable by users (such as Reporting Currency)
   * @see BaseKpiDimensionFieldTypeReadOnly
   */
  ReadOnly = 'ReadOnly',
}

/**
 * DimensionPicker field type configuration
 */
export interface BaseKpiDimensionFieldTypeDimensionPicker {
  type: BaseKpiDimensionFieldTypes.DimensionPicker;
  dimensionName: string;
}

/**
 * Options for Select box field type configuration
 */
export interface BaseKpiDimensionFieldTypeSelectOption {
  /**
   * Optional. When provided, used as the display text. When not provided, the value is also used as the label.
   */
  label?: string;
  /**
   * The value to be used for the field.
   */
  value: string;
}

/**
 * Select box field type configuration
 */
export interface BaseKpiDimensionFieldTypeSelect {
  type: BaseKpiDimensionFieldTypes.Select;
  options: BaseKpiDimensionFieldTypeSelectOption[];
  /**
   * Which value (from `options`) is considered the root - meaning that it does not need to be included in KPI IDs.
   */
  rootValue: string;
}

/**
 * ReadOnly field type configuration
 */
export interface BaseKpiDimensionFieldTypeReadOnly {
  type: BaseKpiDimensionFieldTypes.ReadOnly;
  // When provided, this value should always be used for the DimensionField. (i.e.: USD for Reporting Currency)
  staticValue?: string;
}

/**
 * Union Field type configuration for Deckline Dimensions
 */
export type BaseKpiDimensionFieldType =
  BaseKpiDimensionFieldTypeDimensionPicker | BaseKpiDimensionFieldTypeSelect | BaseKpiDimensionFieldTypeReadOnly;

/**
 * Deckline Dimension Configuration
 */
export interface KpiDimensionConfig<T = KpiDimension> {
  key: T;
  label: string;
  // The key formatted as it is seen in a KPI ID / Formula. (e.g.: revgp is 'REVGP', account is 'Account')
  formulaKey?: string;
  baseKpiField: BaseKpiDimensionFieldType;
  decklineDrivers?: {
    hierarchyAccessor?: keyof KPIDrivers;
    valueAccessor: keyof KPIDrivers;
  };
}
/**
 * Available Dimensions for KPI definitions
 * The values here match the properties found in {@link KPIDrivers} from the API.
 */
export enum KpiDimension {
  ACCOUNT = 'account',
  LOCATION = 'location',
  REVGP = 'revgp',
  COST_CENTER = 'costcenter',
  COMPANY = 'company',
  PROJECT = 'project',
  REPORTING_CURRENCY = 'reporting_currency',
  CHANNEL = 'channel',
  PRODUCT = 'product',
  COST_TYPE = 'costtype',
  COST_POOL = 'costpool',
  DATA_SOURCE = 'datasource',
}

/**
 * Generic Dimension Mapping Configuration
 */
export type DimensionConfigMap<T extends string | number | symbol> = { [key in T]: KpiDimensionConfig<T> };

/**
 * Deckline Dimension Mapping Configuration
 */
export type KpiDimensionConfigMap = DimensionConfigMap<KpiDimension>;

/**
 * Base KPI Panel Props
 */
export interface BaseKpiPanelProps {
  /**
   * The segment/dimension display data
   */
  segmentDisplayData: DriverDisplayOptionMap;
  /**
   * Whether editing is disabled due to deck lock
   */
  isEditingDisabled?: boolean;
  /**
   * Triggered when customer clicks on the toggle switch for a given dimension segment
   * @param index - the index of the segment in the segmentDisplayData array
   * @param checked - the new value for the switch state
   */
  onToggleSwitch?: (key: KpiDimension, checked: boolean) => void;
  /**
   * Triggered when customer clicks on the edit button for a given dimension segment
   * @param index - the index of the segment in the segmentDisplayData array
   * @param segmentData - the segment data for the given index
   */
  onEditStart?: (key: KpiDimension, segmentData: DriverDisplayOptionEntry) => void;
  /**
   * Triggered when customer confirms a change for a given dimension segment
   * @param index - the index of the segment in the segmentDisplayData array
   * @param segmentData - the segment data for the given index
   * @param updates - the KPI drivers values which have changed.
   */
  onChange?: (
    key: KpiDimension,
    segmentData: DriverDisplayOptionEntry,
    updates: Partial<KPIDrivers>,
    newKpiId: string,
  ) => void;
}

/**
 * Calculated KPI Panel Props
 */
export interface CalculatedKPIPanelProps {
  onFormulaChange?: (newId: string, newFormula: string) => void;
  /**
   * The initial formula that will be used to render the state of the Calculated KPI Panel
   */
  initialFormula?: string;

  initialKpiId?: string | null;

  pageDrivers: DriverDisplayOptionMap;

  /**
   * When provided, ensures the KpiDimensions are always included in the generated formulas (even if they are rootNodes)
   */
  alwaysInclude?: string[];

  disabled?: boolean;
}

/**
 * Page Denominator Panel Props
 */
export interface PageDenominatorPanelProps {
  /**
   * Whether editing is disabled due to deck lock
   */
  isEditingDisabled?: boolean;
}
